import { BrowserSessionApiClient } from './browserSessionApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');

export type FinanceMonthlyPoint = {
  month: string;
  label: string;
  quoted: number;
  accepted: number;
  invoiced: number;
  collected: number;
  quoteCount: number;
  acceptedCount: number;
  invoiceCount: number;
  paymentCount: number;
};

export type FinancePeriodPreset =
  | 'rolling-1'
  | 'rolling-3'
  | 'rolling-6'
  | 'rolling-12'
  | 'rolling-18'
  | 'rolling-24'
  | 'financial-year-current'
  | 'financial-year-last'
  | 'year-to-date';

export type FinanceDashboard = {
  period: {
    preset: FinancePeriodPreset;
    label: string;
    months: number;
    start: string;
    end: string;
    timeZone: string;
  };
  currency: string;
  amountBasis: string;
  account: string;
  invoiceSource: 'current' | 'civil';
  totals: {
    quoted: number;
    accepted: number;
    invoiced: number;
    collected: number;
    outstanding: number;
    overdue: number;
    stripeFees: number;
    refunds: number;
    quoteCount: number;
    acceptedCount: number;
    invoiceCount: number;
    paymentCount: number;
    overdueCount: number;
    acceptanceRate: number;
    averageDaysToPay: number | null;
  };
  monthly: FinanceMonthlyPoint[];
  ageing: Array<{ key: string; label: string; count: number; amount: number }>;
  topClients: Array<{ client: string; amount: number }>;
  revenueByCategory: Array<{ category: string; amount: number }>;
  paymentMethods: Array<{ method: string; amount: number }>;
  dataQuality: {
    actualPaymentCount: number;
    estimatedPaymentCount: number;
    paymentsAreEstimated: boolean;
    acceptedDateFallbackUsed: boolean;
  };
};

export type CreatePaymentTransactionInput = {
  invoiceId: number;
  paidAt?: string | null;
  amountReceived: number;
  paymentMethod?: string | null;
  externalReference?: string | null;
  stripeFee?: number;
  refundedAmount?: number;
  source?: string;
};

export type ReconcileInvoiceCandidate = {
  invoiceId: number;
  invoiceNumber: string;
  client: string;
  originalTotal: number;
  outstandingBalance: number;
  issueDate: string | null;
  score: number;
  confidence: 'high' | 'medium' | 'low';
  reasons: string[];
  signals: string[];
  alreadyPaid: boolean;
  rfiTicketNumber?: string;
  projectAddress?: string;
};

export type ReconcilePreviewRow = {
  transactionId: string;
  date: string;
  year: number;
  month: number;
  monthKey: string;
  description: string;
  amount: number;
  page: number;
  sourceOrder: number;
  fileName: string;
  fileHash: string;
  duplicate: boolean;
  duplicateReason: string | null;
  excludedReason: string | null;
  unavailableReason: string | null;
  status: 'ready' | 'review' | 'manual' | 'partial' | 'unavailable' | 'unmatched' | 'excluded' | 'duplicate' | 'paid';
  candidates: ReconcileInvoiceCandidate[];
  suggestedAllocations?: ReconcileAllocation[];
};

export type ReconcileAllocation = { invoiceId: number; amountReceived: number };

export type ReconcilePreview = {
  account: string;
  invoiceSource: 'current' | 'civil';
  sort: 'year-month-date-ascending';
  files: Array<{
    fileName: string;
    fileHash?: string;
    reportStart?: string;
    reportEnd?: string;
    accountLast4?: string | null;
    totalPages?: number;
    reportedDepositTotal?: number;
    extractedDepositTotal?: number;
    depositCount?: number;
    status: 'parsed' | 'error';
    error?: string;
  }>;
  rows: ReconcilePreviewRow[];
  allocationInvoices?: ReconcileInvoiceCandidate[];
  unpaidInvoiceCount: number;
  summary: {
    totalDeposits: number;
    totalAmount: number;
    ready: number;
    review: number;
    manual: number;
    partial: number;
    unavailable: number;
    unmatched: number;
    excluded: number;
    duplicate: number;
    paid: number;
  };
  batchId?: string | null;
  batchStatus?: ReconcileBatchStatus;
  batchUpdatedAt?: string;
  batchExpiresAt?: string;
  persistenceAvailable?: boolean;
  decisions?: ReconcileBatchDecisions;
};

export type ReconcileBatchStatus = 'draft' | 'in_progress' | 'completed';

export type ReconcileBatchDecisions = {
  selectedRowKeys: string[];
  invoiceSelections: Record<string, number>;
  allocationSelections?: Record<string, ReconcileAllocation[]>;
  appliedTransactionIds: string[];
};

export type ReconcileBatchSummary = {
  batchId: string;
  status: ReconcileBatchStatus;
  fileNames: string[];
  fileCount: number;
  transactionCount: number;
  totalAmount: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
};

export type ApplyReconcilePaymentInput = {
  transactionId: string;
  invoiceId?: number;
  allocations?: ReconcileAllocation[];
  paidAt: string;
  amountReceived: number;
  fileName: string;
  page: number;
  description: string;
};

type ApiEnvelope<T> = { success: true; data: T };

class FinanceService {
  private readonly client = new BrowserSessionApiClient(API_BASE_URL);

  async getDashboard(period: FinancePeriodPreset = 'rolling-12') {
    const response = await this.client.request<ApiEnvelope<FinanceDashboard>>(`/api/v1/finance/dashboard?period=${encodeURIComponent(period)}`);
    return response.data;
  }

  async createPaymentTransaction(input: CreatePaymentTransactionInput) {
    const response = await this.client.request<ApiEnvelope<{
      transaction: Record<string, unknown> | null;
      duplicate: boolean;
      invoiceId?: number;
      netReceived?: number;
      invoiceTotal?: number;
      paidInFull?: boolean;
    }>>('/api/v1/payment-transactions', {
      method: 'POST',
      body: input,
    });
    return response.data;
  }

  async previewReconciliation(files: File[]) {
    const form = new FormData();
    files.forEach((file) => form.append('files', file, file.name));
    const response = await this.client.request<ApiEnvelope<ReconcilePreview>>('/api/v1/reconcile/preview', {
      method: 'POST',
      body: form,
    });
    return response.data;
  }

  async listReconciliationBatches() {
    const response = await this.client.request<ApiEnvelope<{
      available: boolean;
      batches: ReconcileBatchSummary[];
    }>>('/api/v1/reconcile/batches');
    return response.data;
  }

  async getReconciliationBatch(batchId: string) {
    const response = await this.client.request<ApiEnvelope<ReconcilePreview>>(`/api/v1/reconcile/batches/${encodeURIComponent(batchId)}`);
    return response.data;
  }

  async updateReconciliationBatch(batchId: string, decisions: ReconcileBatchDecisions) {
    const response = await this.client.request<ApiEnvelope<{
      batchId: string;
      batchStatus: ReconcileBatchStatus;
      batchUpdatedAt: string;
      batchExpiresAt: string;
      decisions: ReconcileBatchDecisions;
    }>>(`/api/v1/reconcile/batches/${encodeURIComponent(batchId)}`, {
      method: 'PATCH',
      body: decisions,
    });
    return response.data;
  }

  async deleteReconciliationBatch(batchId: string) {
    const response = await this.client.request<ApiEnvelope<{ batchId: string; deleted: boolean }>>(`/api/v1/reconcile/batches/${encodeURIComponent(batchId)}`, {
      method: 'DELETE',
    });
    return response.data;
  }

  async applyReconciliation(payments: ApplyReconcilePaymentInput[], batchId?: string | null) {
    const path = batchId
      ? `/api/v1/reconcile/batches/${encodeURIComponent(batchId)}/apply`
      : '/api/v1/reconcile/apply';
    const response = await this.client.request<ApiEnvelope<{
      appliedCount: number;
      duplicateCount: number;
      results: Array<{
        transactionId: string;
        invoiceId: number;
        paymentId: number;
        duplicate: boolean;
      }>;
      invoices: Array<{
        invoiceId: number;
        netReceived: number;
        invoiceTotal: number;
        paidInFull: boolean;
      }>;
    }>>(path, {
      method: 'POST',
      body: { payments },
    });
    return response.data;
  }
}

export const financeService = new FinanceService();
